'use client'

import { useMemo, useState, useEffect } from 'react'
import { cn } from '@/lib/tailwind'
import { groupByTemporalRange } from '@/lib/date-grouping'
import { useVirtualList } from '@/lib/use-virtual-list'
import { CaretUpIcon } from '@phosphor-icons/react'
import type { FeedEntry } from './types'

// Item type for flattened virtual list
type TimelineItem<T> =
  | { type: 'header'; dateKey: string; displayLabel: string }
  | { type: 'entry'; data: T }

interface TimelineViewProps<
  T extends { timestamp: Date; data: { id: string } },
> {
  entries: T[]
  renderEntry: (entry: T) => React.ReactNode
  emptyState?: React.ReactNode
  className?: string
}

// Internal DateHeader component with sticky positioning and refined animation
function DateHeader({ label }: { label: string }) {
  return (
    <div
      className={cn(
        // Position and layering
        'sticky top-0 z-10',
        // Background with subtle blur
        'bg-background/95 backdrop-blur-sm',
        // Spacing
        'px-4 py-2.5',
        // Typography - refined and subtle, dimmed for content prominence
        'text-muted-foreground/70 text-sm font-medium tracking-wide uppercase',
      )}
    >
      {label}
    </div>
  )
}

export function TimelineView<T extends FeedEntry>({
  entries,
  renderEntry,
  emptyState,
  className,
}: TimelineViewProps<T>) {
  // Show scroll-to-top button after scrolling down
  const [showScrollTop, setShowScrollTop] = useState(false)

  // Group entries by date and flatten into timeline items
  const timelineItems = useMemo(() => {
    const groups = groupByTemporalRange(entries)
    const items: TimelineItem<T>[] = []

    // Flatten groups: first add header, then all entries for that date
    groups.forEach((group) => {
      // Add date header
      items.push({
        type: 'header',
        dateKey: group.date,
        displayLabel: group.displayLabel,
      })

      // Add all entries for this date
      group.entries.forEach((entry) => {
        items.push({
          type: 'entry',
          data: entry as T,
        })
      })
    })

    return items
  }, [entries])

  // Setup virtual scrolling
  const { containerRef, virtualItems, totalSize, measureElement } =
    useVirtualList({
      items: timelineItems,
      estimateSize: 48, // Headers: ~40px, Entries: ~48px collapsed (dynamic measurement)
      getItemKey: (index, item) => {
        if (item.type === 'header') {
          return `header-${item.dateKey}`
        }
        // Entry key format: `${entry.type}-${entry.data.id}`
        const entry = item.data
        return `${entry.type}-${entry.data.id}`
      },
    })

  // Handle scroll events for scroll-to-top button (window scroll)
  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling 400px down
      setShowScrollTop(window.scrollY > 400)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Empty state handling
  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <div className="relative">
      <div ref={containerRef} className={cn(className)}>
        <div
          style={{
            height: `${totalSize}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualItem) => {
            const item = timelineItems[virtualItem.index]
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {item.type === 'header' ? (
                  <DateHeader label={item.displayLabel} />
                ) : (
                  <div className="py-0.5">{renderEntry(item.data)}</div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className={cn(
            // Position - fixed in bottom right
            'fixed right-6 bottom-6',
            // Appearance - simple circular button
            'h-10 w-10 rounded-full',
            'bg-primary text-primary-foreground',
            'shadow-md hover:shadow-lg',
            // Interaction
            'transition-shadow duration-200',
            // Focus states
            'focus:ring-primary focus:ring-2 focus:ring-offset-2 focus:outline-none',
          )}
          aria-label="Scroll to top"
        >
          <CaretUpIcon className="mx-auto h-4 w-4" weight="bold" />
        </button>
      )}
    </div>
  )
}
